import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  linkedSignal,
  output,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { EventqScrollArea } from '../../primitives/scroll-area/scroll-area';
import type {
  EventqSearchableListAppearance,
  EventqSearchableListColumn,
  EventqSearchableListRow,
  EventqSearchableListRowAction,
  EventqSearchableListSelectionMode,
} from './searchable-list.types';

export type {
  EventqSearchableListAppearance,
  EventqSearchableListColumn,
  EventqSearchableListColumnStyle,
  EventqSearchableListColumnType,
  EventqSearchableListRow,
  EventqSearchableListRowAction,
  EventqSearchableListSelectionMode,
} from './searchable-list.types';

const DEFAULT_SKELETON_COUNT = 3;
/** Pixels from the bottom of the list before the next page is revealed. */
const LOAD_MORE_THRESHOLD_PX = 80;

@Component({
  selector: 'eventq-searchable-list',
  imports: [MatIcon, EventqScrollArea],
  templateUrl: './searchable-list.html',
  styleUrl: './searchable-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'eventq-searchable-list-host',
    '[class.eventq-searchable-list-host--popover]': "appearance() === 'popover'",
    '[class.eventq-searchable-list-host--panel]': "appearance() === 'panel'",
    '[class.eventq-searchable-list-host--camera]': "appearance() === 'camera'",
    '[class.eventq-searchable-list-host--fill]': 'fillHeight()',
    '[attr.aria-busy]': "loading() ? 'true' : 'false'",
  },
})
export class EventqSearchableList {
  readonly columns = input.required<readonly EventqSearchableListColumn[]>();
  readonly rows = input<readonly EventqSearchableListRow[]>([]);
  readonly selectionMode = input<EventqSearchableListSelectionMode>('single');
  /**
   * When false, rows are display-only (no selection). Action cells still emit.
   * Used for view-only site lists and camera rows driven only by trailing actions.
   */
  readonly selectable = input(true);
  readonly selectedIds = input<readonly string[]>([]);
  readonly searchQuery = input('');
  readonly searchPlaceholder = input('Search');
  readonly showSearch = input(true);
  /** When false, skip the default row list so a projected body can replace it. */
  readonly showRows = input(true);
  readonly loading = input(false);
  readonly skeletonCount = input<number | null>(null);
  readonly emptyMessage = input('No results');
  readonly appearance = input<EventqSearchableListAppearance>('popover');
  readonly listAriaLabel = input('Results');
  /** When true, the scroll region fills remaining host height. */
  readonly fillHeight = input(false);
  /** Fixed scroll viewport height (e.g. `339px`). Ignored when `fillHeight` is true. */
  readonly listHeight = input<string | null>(null);
  /**
   * When set, only the first N rows are rendered; more are revealed as the user
   * scrolls near the bottom. Parent still passes the full filtered list — this
   * is client-side windowing, not server pagination. `null` renders every row.
   */
  readonly pageSize = input<number | null>(null);

  readonly searchQueryChange = output<string>();
  /** Fired in single-select when a row is chosen (redirect / navigate hook). */
  readonly itemSelect = output<string>();
  readonly selectedIdsChange = output<readonly string[]>();
  readonly rowAction = output<EventqSearchableListRowAction>();

  readonly textColumns = computed(() =>
    this.columns().filter((column) => (column.type ?? 'text') === 'text'),
  );
  readonly actionColumns = computed(() =>
    this.columns().filter((column) => column.type === 'action'),
  );

  /**
   * Visible window size. Resets whenever the incoming `rows` identity changes
   * (search / filter / variant switch) so a new result set always starts at the
   * first page.
   */
  readonly visibleCount = linkedSignal({
    source: this.rows,
    computation: () => {
      const size = this.pageSize();
      return size == null || size <= 0 ? Number.POSITIVE_INFINITY : size;
    },
  });

  readonly visibleRows = computed(() => {
    const all = this.rows();
    const size = this.pageSize();
    if (size == null || size <= 0) {
      return all;
    }
    return all.slice(0, this.visibleCount());
  });

  readonly skeletonIndexes = computed(() => {
    const explicit = this.skeletonCount();
    const count =
      explicit != null && explicit > 0
        ? explicit
        : this.rows().length > 0
          ? this.rows().length
          : DEFAULT_SKELETON_COUNT;
    return Array.from({ length: count }, (_, index) => index);
  });

  readonly scrollStyle = computed(() => {
    if (this.fillHeight()) {
      return {};
    }
    const height = this.listHeight();
    return height ? { height } : {};
  });

  cellText(row: EventqSearchableListRow, key: string): string {
    const value = row[key];
    if (value == null) {
      return '';
    }
    return String(value);
  }

  leadingIcon(row: EventqSearchableListRow): string | null {
    const value = row['leadingIcon'];
    return typeof value === 'string' && value.length > 0 ? value : null;
  }

  leadingIconTone(row: EventqSearchableListRow): 'armed' | 'disarmed' | null {
    const value = row['leadingIconTone'];
    return value === 'armed' || value === 'disarmed' ? value : null;
  }

  actionIcon(row: EventqSearchableListRow, column: EventqSearchableListColumn): string {
    const key = column.actionIconKey ?? 'actionIcon';
    const fromRow = row[key];
    if (typeof fromRow === 'string' && fromRow.length > 0) {
      return fromRow;
    }
    return column.actionIcon ?? 'add';
  }

  actionLabel(row: EventqSearchableListRow, column: EventqSearchableListColumn): string {
    const key = column.actionLabelKey ?? 'actionLabel';
    const fromRow = row[key];
    if (typeof fromRow === 'string' && fromRow.length > 0) {
      return fromRow;
    }
    return column.actionLabel ?? 'Action';
  }

  isSelected(id: string): boolean {
    return this.selectedIds().includes(id);
  }

  isActionActive(row: EventqSearchableListRow): boolean {
    return row['actionActive'] === true;
  }

  onSearchInput(event: Event): void {
    this.searchQueryChange.emit((event.target as HTMLInputElement).value);
  }

  onListScroll(event: Event): void {
    const pageSize = this.pageSize();
    if (pageSize == null || pageSize <= 0) {
      return;
    }

    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight > LOAD_MORE_THRESHOLD_PX) {
      return;
    }

    const total = this.rows().length;
    this.visibleCount.update((current) => Math.min(current + pageSize, total));
  }

  onRowActivate(row: EventqSearchableListRow): void {
    if (!this.selectable()) {
      return;
    }

    const id = row.id;
    if (this.selectionMode() === 'single') {
      this.itemSelect.emit(id);
      this.selectedIdsChange.emit([id]);
      return;
    }

    const current = this.selectedIds();
    const next = current.includes(id)
      ? current.filter((selectedId) => selectedId !== id)
      : [...current, id];
    this.selectedIdsChange.emit(next);
  }

  onActionClick(
    event: Event,
    row: EventqSearchableListRow,
    column: EventqSearchableListColumn,
  ): void {
    event.stopPropagation();
    this.rowAction.emit({ id: row.id, columnKey: column.key });
  }
}
